/**
 * Haptic Feedback Utility
 * Provides tactile feedback for interactions on supported devices.
 * Uses the Vibration API which is supported on most mobile browsers.
 */

export type HapticType = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

const hapticPatterns: Record<HapticType, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 40,
  selection: 5,
  success: [10, 50, 10],
  warning: [15, 30, 15, 30, 15],
  error: [30, 50, 30],
};

let isSupported: boolean | null = null;

/**
 * Checks if the Vibration API is supported
 */
export function isHapticSupported(): boolean {
  if (isSupported !== null) return isSupported;
  isSupported = 'vibrate' in navigator;
  return isSupported;
}

/**
 * Triggers haptic feedback
 * @param type - Type of haptic feedback
 */
export function hapticFeedback(type: HapticType = 'light'): void {
  if (!isHapticSupported()) return;

  const pattern = hapticPatterns[type];
  try {
    navigator.vibrate(pattern);
  } catch {
    // Silently fail if vibration is not allowed
  }
}

/**
 * Triggers light haptic feedback (for button presses, switch toggles)
 */
export function hapticLight(): void {
  hapticFeedback('light');
}

/**
 * Triggers medium haptic feedback (for important actions)
 */
export function hapticMedium(): void {
  hapticFeedback('medium');
}

/**
 * Triggers heavy haptic feedback (for destructive actions)
 */
export function hapticHeavy(): void {
  hapticFeedback('heavy');
}

/**
 * Triggers selection haptic feedback (for picker changes, tab switches)
 */
export function hapticSelection(): void {
  hapticFeedback('selection');
}

/**
 * Triggers success haptic feedback
 */
export function hapticSuccess(): void {
  hapticFeedback('success');
}

/**
 * Triggers warning haptic feedback
 */
export function hapticWarning(): void {
  hapticFeedback('warning');
}

/**
 * Triggers error haptic feedback
 */
export function hapticError(): void {
  hapticFeedback('error');
}